import { Link } from 'react-router-dom';
import { Layout } from '../components/layout';
import { useScrollReveal } from '../hooks/useScrollReveal';
import GoldilocksZoneDiagram from '../components/GoldilocksZoneDiagram';
import { SEO, BookSchema } from '../components/SEO';

// Asset URLs
const BOOK_COVER_URL = "https://customer-assets.emergentagent.com/job_87621aa9-388b-4a4a-b140-4ad09ad180a4/artifacts/6pn8cn64_ec5aa56a-061d-4137-8640-f07bb270fd5e.png";

const keyConcepts = [
  "A diagnostic for reading your organisation's temperature and its genuine readiness for change",
  "The anti-patterns and hidden distortions that quietly stop transformation from taking root",
  "A collective intelligence framework that makes adaptation a property of the whole organisation",
  "The GIFT framework: four reinforcing leadership behaviours, Gratitude, Inclusion, Forgiveness, and Trust, built on a foundation of Alignment and sustained through Reinforcement"
];

const HeroSection = () => {
  return (
    <section 
      className="min-h-[35vh] flex items-center bg-[#faf9f7]"
      data-testid="goldilocks-zone-hero"
    >
      <div className="max-w-4xl mx-auto px-6 lg:px-8 py-16 md:py-20">
        <h1 
          className="font-playfair text-4xl sm:text-5xl lg:text-6xl font-bold italic text-[#1a1a1a] mb-6 animate-fade-in-up"
          data-testid="goldilocks-zone-hero-title"
        >
          The Goldilocks Zone
        </h1>
        <p 
          className="font-dm-sans text-lg md:text-xl text-[#4a4744] leading-relaxed max-w-3xl animate-fade-in-up animate-delay-200"
          data-testid="goldilocks-zone-hero-subtitle"
        >
          How leaders build the conditions for continuous evolution.
        </p>
      </div>
    </section>
  );
};

const BookSection = () => {
  const [ref, isVisible] = useScrollReveal(0.1);

  return (
    <section className="pt-12 pb-20 md:pt-16 md:pb-28 bg-[#FFFFFF]" data-testid="book-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div 
          ref={ref}
          className={`grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8 items-start transition-all duration-700 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          {/* Book Cover - Left (no shadow, image has baked-in shadow) */}
          <div className="flex justify-center lg:justify-start lg:pl-[25%]">
            <div className="max-w-sm">
              <img 
                src={BOOK_COVER_URL} 
                alt="The Goldilocks Zone Book Cover"
                className="w-full"
                data-testid="book-cover-image"
              />
            </div>
          </div>

          {/* Content - Right */}
          <div>
            <p className="text-[#d4782c] font-dm-sans font-semibold text-sm uppercase tracking-wider mb-3">
              THE BOOK
            </p>
            <h2 className="font-playfair text-3xl md:text-4xl font-bold text-[#1a1a1a] mb-2">
              The Goldilocks Zone
            </h2>
            <p className="font-playfair text-xl italic text-[#4a4744] mb-4">
              How Leaders Build the Conditions for Continuous Evolution
            </p>
            <p className="text-[#d4782c] font-dm-sans font-medium mb-6">
              By Nadir Khan, founder of Agilitie, and Craig Kimball
            </p>
            
            <div className="text-[#4a4744] leading-relaxed mb-8 space-y-4">
              <p>
                You can pick the right strategy, hire the right people, and run a textbook transformation, and still watch it die. Most leaders do, again and again, and never quite see why.
              </p>
              <p>
                The reason is climate. Every organisation has a temperature. Run it too hot and it burns out in churn and politics. Run it too cold and it calcifies into apathy and inertia. Change only takes hold in the narrow band between, the same way life only flourishes in a planet's habitable zone. The Goldilocks Zone turns that idea into the leader's real work, building the conditions where change takes hold on its own. This is a book about climate management, not change management.
              </p>
            </div>

            {/* Key Concepts */}
            <div className="mb-8">
              <p className="text-[#1a1a1a] font-dm-sans font-semibold text-sm uppercase tracking-wider mb-4">
                Key Concepts
              </p>
              <ul className="space-y-3">
                {keyConcepts.map((concept, index) => (
                  <li key={`concept-${index}-${concept.substring(0, 20)}`} className="flex items-start gap-3">
                    <span className="text-[#d4782c] mt-1">•</span>
                    <span className="text-[#4a4744]">{concept}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Availability Line */}
            <p className="text-[#6b6b6b] text-sm mb-4">
              Now available to pre-order in Kindle, paperback and hardcover. Launching 21 July 2026.
            </p>

            <a 
              href="https://thegoldilockszone.com.au/buy" 
              target="_blank" 
              rel="noopener noreferrer"
              className="btn-primary inline-block"
              data-testid="pre-order-book-btn"
            >
              Pre-Order the Book
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

// Interactive Goldilocks Zone Diagram Section
const DiagramSection = () => {
  const [ref, isVisible] = useScrollReveal(0.1);

  return (
    <section className="py-16 md:py-24 bg-[#1a1a1a]" data-testid="diagram-section">
      <div className="max-w-6xl mx-auto px-6 lg:px-8">
        <div
          ref={ref}
          className={`transition-all duration-700 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <GoldilocksZoneDiagram />
        </div>
      </div>
    </section>
  );
};

const CTASection = () => {
  const [ref, isVisible] = useScrollReveal(0.1);

  return (
    <section className="py-20 md:py-24 bg-[#faf9f7]" data-testid="cta-section">
      <div className="max-w-4xl mx-auto px-6 lg:px-8">
        <div 
          ref={ref}
          className={`text-center transition-all duration-700 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <h2 className="font-playfair text-3xl md:text-4xl font-bold text-[#1a1a1a] mb-6">
            Ready to find your organisation's sweet spot?
          </h2>
          <p className="text-[#4a4744] text-lg leading-relaxed mb-10 max-w-2xl mx-auto">
            Let's talk about how the Goldilocks Zone framework can help your organisation create the conditions for lasting change.
          </p>
          <Link 
            to="/contact" 
            className="btn-primary inline-block"
            data-testid="cta-contact-btn"
          >
            Get in Touch
          </Link>
        </div>
      </div>
    </section>
  );
};

export default function GoldilocksZonePage() {
  return (
    <Layout>
      <SEO 
        title="The Goldilocks Zone"
        path="/the-goldilocks-zone"
        description="The Goldilocks Zone by Nadir Khan and Craig Kimball — a new framework for organisational transformation. How leaders build the conditions for continuous evolution."
      />
      <BookSchema />
      <HeroSection />
      <BookSection />
      <DiagramSection />
      <CTASection />
    </Layout>
  );
}
